import React, { useEffect, useRef } from "react";

/**
 * Toast — slide-up notification (success / error / warning / info)
 *
 * Usage:
 *   const [toast, setToast] = useState(null);
 *   setToast({ type: "ok", msg: "Byagenze neza!" });
 *   setToast({ type: "err", msg: "Habaye ikosa." });
 *   setToast({ type: "warn", msg: "Reba neza." });
 *   setToast({ type: "info", msg: "Amakuru mashya." });
 *
 *   <Toast toast={toast} onClose={() => setToast(null)} />
 */

const CONFIGS = {
  ok: {
    bg: "rgba(32,96,61,0.08)",
    border: "rgba(32,96,61,0.28)",
    color: "#20603D",
    icon: "bi-check-circle-fill",
    bar: "#20603D",
  },
  err: {
    bg: "rgba(192,57,43,0.08)",
    border: "rgba(192,57,43,0.28)",
    color: "#c0392b",
    icon: "bi-exclamation-triangle-fill",
    bar: "#c0392b",
  },
  warn: {
    bg: "rgba(224,123,0,0.08)",
    border: "rgba(224,123,0,0.28)",
    color: "#b45309",
    icon: "bi-exclamation-circle-fill",
    bar: "#e07b00",
  },
  info: {
    bg: "rgba(0,71,171,0.08)",
    border: "rgba(0,71,171,0.25)",
    color: "#0047AB",
    icon: "bi-info-circle-fill",
    bar: "#0047AB",
  },
};

export function Toast({ toast, onClose, duration = 4500 }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!toast) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onClose, duration);
    return () => clearTimeout(timerRef.current);
  }, [toast, onClose, duration]);

  if (!toast) return null;

  const cfg = CONFIGS[toast.type] || CONFIGS.info;

  return (
    <>
      <style>{`
        @keyframes _toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(16px) scale(0.95); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1);    }
        }
        @keyframes _toastBar {
          from { width: 100%; }
          to   { width: 0%; }
        }
        ._toast-wrap {
          position: fixed;
          bottom: 88px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 9999;
          min-width: 280px;
          max-width: min(92vw, 440px);
          animation: _toastIn 0.32s cubic-bezier(0.34,1.56,0.64,1) both;
          pointer-events: auto;
        }
        ._toast-card {
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 12px 16px;
          border-radius: 9px;
          border: 1px solid;
          box-shadow: 0 6px 24px rgba(0,0,0,0.12);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          position: relative;
          overflow: hidden;
        }
        ._toast-icon { font-size: 1.05rem; flex-shrink: 0; }
        ._toast-msg  { flex: 1; font-size: 0.84rem; font-weight: 600; line-height: 1.4; font-family: 'Nunito Sans', sans-serif; }
        ._toast-close {
          background: none; border: none; cursor: pointer;
          font-size: 0.85rem; opacity: 0.55; transition: opacity 0.2s;
          padding: 4px; flex-shrink: 0; line-height: 1;
        }
        ._toast-close:hover { opacity: 1; }
        ._toast-bar {
          position: absolute;
          bottom: 0; left: 0;
          height: 3px;
          border-radius: 0 0 9px 9px;
          animation: _toastBar linear both;
        }
      `}</style>

      <div className="_toast-wrap" role="alert" aria-live="polite">
        <div
          className="_toast-card"
          style={{
            background: cfg.bg,
            borderColor: cfg.border,
            color: cfg.color,
          }}
        >
          <i className={`bi ${cfg.icon} _toast-icon`} />
          <span className="_toast-msg">{toast.msg}</span>
          <button className="_toast-close" onClick={onClose} style={{ color: cfg.color }}>
            <i className="bi bi-x-lg" />
          </button>
          <div
            className="_toast-bar"
            style={{
              background: cfg.bar,
              animationDuration: `${duration}ms`,
            }}
          />
        </div>
      </div>
    </>
  );
}

/**
 * InlineAlert — replaces Bootstrap <Alert> inside forms/cards
 *
 * <InlineAlert type="err" msg="Habaye ikosa" onClose={() => setErr("")} />
 */
export function InlineAlert({ type = "err", msg, onClose }) {
  if (!msg) return null;
  const cfg = CONFIGS[type] || CONFIGS.err;

  return (
    <>
      <style>{`
        @keyframes _alertIn {
          from { opacity: 0; transform: translateY(-8px); max-height: 0; }
          to   { opacity: 1; transform: translateY(0);    max-height: 120px; }
        }
        @keyframes _alertShake {
          0%,100% { transform: translateX(0); }
          20%     { transform: translateX(-5px); }
          40%     { transform: translateX(5px); }
          60%     { transform: translateX(-3px); }
          80%     { transform: translateX(3px); }
        }
        ._inline-alert {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 11px 14px;
          border-radius: 8px;
          border: 1px solid;
          margin-bottom: 1rem;
          animation: _alertIn 0.28s ease, _alertShake 0.4s ease;
          position: relative;
          overflow: hidden;
        }
        ._inline-alert::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          border-radius: 8px 0 0 8px;
        }
        ._inline-alert-icon { font-size: 1rem; flex-shrink: 0; margin-top: 1px; }
        ._inline-alert-text { flex: 1; font-size: 0.85rem; font-weight: 500; line-height: 1.45; font-family: 'Nunito Sans', sans-serif; }
        ._inline-alert-close {
          background: none; border: none; cursor: pointer;
          font-size: 0.8rem; opacity: 0.5; transition: opacity 0.2s;
          padding: 2px; flex-shrink: 0;
        }
        ._inline-alert-close:hover { opacity: 1; }
      `}</style>

      <div
        className="_inline-alert"
        role="alert"
        style={{
          background: cfg.bg,
          borderColor: cfg.border,
          color: cfg.color,
        }}
      >
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: cfg.bar, borderRadius: "8px 0 0 8px" }} />
        <i className={`bi ${cfg.icon} _inline-alert-icon`} style={{ marginLeft: 6 }} />
        <span className="_inline-alert-text">{msg}</span>
        {onClose && (
          <button className="_inline-alert-close" onClick={onClose} style={{ color: cfg.color }}>
            <i className="bi bi-x-lg" />
          </button>
        )}
      </div>
    </>
  );
}

/**
 * SuccessOverlay — full-screen animated success modal
 *
 * <SuccessOverlay
 *   show={showSuccess}
 *   title="Byagenze Neza!"
 *   subtitle="Umuyobozi yanditswe neza."
 *   name={formData.fullname}
 *   onClose={() => setShowSuccess(false)}
 * />
 */
export function SuccessOverlay({ show, title = "Byagenze Neza!", subtitle, name, onClose, duration = 4500 }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!show || !onClose) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onClose, duration);
    return () => clearTimeout(timerRef.current);
  }, [show, onClose, duration]);

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes _overlayIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes _cardPop    { from { opacity: 0; transform: scale(0.6) translateY(30px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes _iconSpin   { from { opacity: 0; transform: scale(0) rotate(-180deg); } to { opacity: 1; transform: scale(1) rotate(0deg); } }
        @keyframes _textFade   { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes _barDrain   { from { width: 100%; } to { width: 0%; } }
        @keyframes _sparkle    { 0%,100% { opacity: 0; transform: scale(0) translateY(0); } 50% { opacity: 1; transform: scale(1.2) translateY(-10px); } }

        ._sov-backdrop {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: _overlayIn 0.35s ease;
        }
        ._sov-card {
          background: #ffffff;
          border: 2px solid rgba(32,96,61,0.2);
          border-radius: 18px;
          padding: 2.5rem 2.25rem 2rem;
          text-align: center;
          max-width: 420px; width: 100%;
          box-shadow: 0 24px 64px rgba(0,0,0,0.18);
          position: relative; overflow: hidden;
          animation: _cardPop 0.45s cubic-bezier(0.34,1.56,0.64,1) 0.05s both;
        }
        ._sov-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #0047AB, #FAD201, #20603D);
        }
        ._sov-sparkle {
          position: absolute; width: 6px; height: 6px; border-radius: 50%;
          animation: _sparkle 2.5s ease-in-out infinite;
        }
        ._sov-icon-ring {
          width: 84px; height: 84px; border-radius: 50%;
          background: rgba(32,96,61,0.1);
          border: 3px solid rgba(32,96,61,0.35);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1.25rem;
          box-shadow: 0 0 24px rgba(32,96,61,0.15);
          animation: _iconSpin 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.2s both;
        }
        ._sov-title {
          font-size: 1.55rem; font-weight: 800;
          color: #0047AB; margin-bottom: 0.5rem;
          animation: _textFade 0.4s ease 0.45s both;
          font-family: 'Nunito Sans', sans-serif;
        }
        ._sov-subtitle {
          color: #4a5568; font-size: 0.9rem; line-height: 1.55;
          margin-bottom: 0.6rem;
          animation: _textFade 0.4s ease 0.55s both;
          font-family: 'Nunito Sans', sans-serif;
        }
        ._sov-name {
          color: #0047AB; font-weight: 700; font-size: 1rem;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          animation: _textFade 0.4s ease 0.65s both;
          font-family: 'Nunito Sans', sans-serif;
        }
        ._sov-bar-track {
          width: 100%; height: 4px; background: #f0f4f8;
          border-radius: 4px; margin-top: 1.5rem; overflow: hidden;
          animation: _textFade 0.4s ease 0.8s both;
        }
        ._sov-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #0047AB, #FAD201);
          border-radius: 4px;
          animation: _barDrain linear both;
        }
        ._sov-hint {
          color: #718096; font-size: 0.76rem; margin-top: 0.6rem;
          animation: _textFade 0.4s ease 0.9s both;
          font-family: 'Nunito Sans', sans-serif;
        }
        ._sov-close {
          position: absolute; top: 14px; right: 14px;
          width: 30px; height: 30px; border-radius: 6px;
          border: 1px solid #e2e8f0; background: #f8fafc;
          color: #64748b; cursor: pointer; font-size: 0.8rem;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s ease;
        }
        ._sov-close:hover { background: #e2e8f0; color: #0d1b2a; }
      `}</style>

      <div className="_sov-backdrop" onClick={onClose}>
        <div className="_sov-card" onClick={(e) => e.stopPropagation()}>
          {/* Sparkles */}
          {[
            { top: "10%", left: "8%",  bg: "#FAD201", delay: "0s" },
            { top: "15%", right: "10%", bg: "#0047AB", delay: "0.6s" },
            { bottom: "20%", left: "12%", bg: "#20603D", delay: "1.2s" },
            { bottom: "12%", right: "8%", bg: "#FAD201", delay: "1.8s" },
          ].map((s, i) => (
            <div key={i} className="_sov-sparkle" style={{ ...s, background: s.bg, animationDelay: s.delay }} />
          ))}

          {onClose && (
            <button className="_sov-close" onClick={onClose}>
              <i className="bi bi-x-lg" />
            </button>
          )}

          <div className="_sov-icon-ring">
            <i className="bi bi-check-circle-fill" style={{ fontSize: "2.4rem", color: "#20603D" }} />
          </div>

          <h2 className="_sov-title">{title}</h2>
          {subtitle && <p className="_sov-subtitle">{subtitle}</p>}
          {name && (
            <div className="_sov-name">
              <i className="bi bi-person-badge" />
              {name}
            </div>
          )}

          <div className="_sov-bar-track">
            <div className="_sov-bar-fill" style={{ animationDuration: `${duration}ms` }} />
          </div>
          <p className="_sov-hint">
            <i className="bi bi-arrow-repeat me-1" />
            Ifungura vuba...
          </p>
        </div>
      </div>
    </>
  );
}

/**
 * ConfirmModal — styled confirmation dialog
 *
 * <ConfirmModal
 *   show={showConfirm}
 *   title="Gusiba"
 *   message="Urashaka koko gusiba uyu muntu?"
 *   confirmLabel="Yego, Siba"
 *   danger
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowConfirm(false)}
 * />
 */
export function ConfirmModal({ show, title, message, confirmLabel = "Emeza", cancelLabel = "Bireke", danger = false, onConfirm, onCancel }) {
  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes _cmIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes _cmCard { from { opacity: 0; transform: scale(0.88) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        ._cm-backdrop {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.52);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 9998;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: _cmIn 0.2s ease;
        }
        ._cm-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 28px 24px;
          text-align: center;
          max-width: 360px; width: 100%;
          box-shadow: 0 16px 48px rgba(0,0,0,0.14);
          animation: _cmCard 0.28s cubic-bezier(0.34,1.56,0.64,1);
        }
        ._cm-icon {
          width: 56px; height: 56px; border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 1.4rem; margin-bottom: 14px;
        }
        ._cm-title { font-size: 1.05rem; font-weight: 800; color: #0d1b2a; margin-bottom: 8px; font-family: 'Nunito Sans', sans-serif; }
        ._cm-msg   { font-size: 0.84rem; color: #4a5568; line-height: 1.5; margin-bottom: 20px; font-family: 'Nunito Sans', sans-serif; }
        ._cm-actions { display: flex; gap: 10px; }
        ._cm-btn {
          flex: 1; padding: 10px 16px; border-radius: 7px;
          font-weight: 650; font-size: 0.84rem; cursor: pointer;
          transition: all 0.2s ease; font-family: 'Nunito Sans', sans-serif;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          border: none;
        }
        ._cm-cancel { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0 !important; }
        ._cm-cancel:hover { background: #e8edf3; color: #0d1b2a; }
        ._cm-confirm-danger { background: #c0392b; color: #fff; box-shadow: 0 3px 12px rgba(192,57,43,0.25); }
        ._cm-confirm-danger:hover { background: #a93226; transform: translateY(-1px); }
        ._cm-confirm-primary { background: #0047AB; color: #fff; box-shadow: 0 3px 12px rgba(0,71,171,0.25); }
        ._cm-confirm-primary:hover { background: #003380; transform: translateY(-1px); }
      `}</style>

      <div className="_cm-backdrop" onClick={onCancel}>
        <div className="_cm-card" onClick={(e) => e.stopPropagation()}>
          <div
            className="_cm-icon"
            style={{
              background: danger ? "rgba(192,57,43,0.1)" : "rgba(0,71,171,0.1)",
              color: danger ? "#c0392b" : "#0047AB",
            }}
          >
            <i className={`bi ${danger ? "bi-trash3-fill" : "bi-question-circle-fill"}`} />
          </div>
          <h3 className="_cm-title">{title}</h3>
          <p className="_cm-msg">{message}</p>
          <div className="_cm-actions">
            <button className="_cm-btn _cm-cancel" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button
              className={`_cm-btn ${danger ? "_cm-confirm-danger" : "_cm-confirm-primary"}`}
              onClick={onConfirm}
            >
              <i className={`bi ${danger ? "bi-trash3" : "bi-check2"}`} />
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Toast;
